import { current, freeze, isDraft, produce, setAutoFreeze } from 'immer';
import type { AggregateDef, AnyEventDef, EventMeta, LegacyEventDef, UndoEvent } from './aggregate.js';
import { assert } from './assert.js';
import { effectiveTimestamp } from './stamp.js';
import type { StoredEvent } from './storeTypes.js';

// Consumers see immutable snapshots; stated here rather than relied on as immer's default.
setAutoFreeze(true);

// A mutation as a consumer commits it, or as a test folds it. `meta` is attached by the engine;
// the pure helpers synthesise a fallback so a reducer never sees undefined.
export interface AnyMutation {
  readonly kind: string;
  readonly payload: unknown;
  readonly meta?: EventMeta | undefined;
}

const FALLBACK_META: EventMeta = { timestamp: 0, clientId: '', userId: undefined, origin: 'local' };

export const EVENTS_PER_FOLD_MAX = 1_000_000;
const RESULT_DEPTH_MAX = 8;
const RESULT_NODES_MAX = 4096;

// Own-property lookup. `kind` comes off the wire, so a plain `map[kind]` would resolve
// `__proto__` or `constructor` to an inherited Function and slip past the unknown-kind guard.
export function lookupKind<T>(map: Record<string, T>, kind: string): T | undefined {
  return Object.prototype.hasOwnProperty.call(map, kind) ? map[kind] : undefined;
}

export class UnknownEventKindError extends Error {
  readonly kind: string;
  constructor(aggregateName: string, kind: string) {
    super(`aggregate "${aggregateName}": no reducer (events or legacyEvents) for kind "${kind}"`);
    this.name = 'UnknownEventKindError';
    this.kind = kind;
  }
}

export interface FoldOutcome<TState> {
  readonly state: TState;
  readonly results: unknown[];
}

interface Decoded {
  readonly kind: string;
  readonly payload: unknown;
}

function decodeValue(value: string): Decoded {
  const parsed = JSON.parse(value) as { kind?: unknown; payload?: unknown };
  assert(typeof parsed.kind === 'string', 'decodeValue: stored value carries a string kind');
  return { kind: parsed.kind, payload: parsed.payload };
}

function metaOf(event: StoredEvent): EventMeta {
  return {
    timestamp: effectiveTimestamp(event.ts, event.serverTs),
    clientId: event.clientId,
    userId: event.userId,
    origin: event.si === 0 ? 'local' : 'remote',
  };
}

// A result may hold draft proxies (a reducer returning `{ old: s.cells[k] }` straight off the
// draft). They are revoked when produce ends, so every draft is replaced with its current value
// inside the recipe. Bounded because a hostile or cyclic result must be an error, not a hang.
export function finaliseResult(value: unknown): unknown {
  const budget = { nodes: 0 };
  const out = finaliseNode(value, 0, budget);
  assert(budget.nodes <= RESULT_NODES_MAX, 'finaliseResult: node budget respected');
  return out;
}

function finaliseNode(value: unknown, depth: number, budget: { nodes: number }): unknown {
  budget.nodes += 1;
  assert(budget.nodes <= RESULT_NODES_MAX, `finaliseResult: a result exceeds ${RESULT_NODES_MAX} nodes`);
  if (isDraft(value)) return current(value);
  if (typeof value !== 'object' || value === null) return value;
  assert(depth < RESULT_DEPTH_MAX, `finaliseResult: a result nests deeper than ${RESULT_DEPTH_MAX}`);
  if (Array.isArray(value)) {
    let copy: unknown[] | undefined;
    for (let i = 0; i < value.length; i += 1) {
      const next = finaliseNode(value[i], depth + 1, budget);
      if (next !== value[i]) {
        copy ??= value.slice();
        copy[i] = next;
      }
    }
    return copy ?? value;
  }
  if (Object.getPrototypeOf(value) !== Object.prototype && Object.getPrototypeOf(value) !== null) return value;
  const record = value as Record<string, unknown>;
  let copy: Record<string, unknown> | undefined;
  for (const key of Object.keys(record)) {
    const next = finaliseNode(record[key], depth + 1, budget);
    if (next !== record[key]) {
      copy ??= { ...record };
      copy[key] = next;
    }
  }
  return copy ?? value;
}

function resolveReducer<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  kind: string,
): AnyEventDef<TState> | LegacyEventDef<TState> {
  const active = lookupKind<AnyEventDef<TState>>(def.events, kind);
  if (active !== undefined) return active;
  const legacy = lookupKind<LegacyEventDef<TState>>(def.legacyEvents, kind);
  if (legacy === undefined) throw new UnknownEventKindError(def.name, kind);
  return legacy;
}

// The fold loop. One produce for the whole batch, in the order given (the caller's log order).
// `collectResults: false` is replay: the results are not wanted and are not finalised.
export function foldEvents<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  state: TState,
  events: ReadonlyArray<StoredEvent>,
  collectResults: boolean,
): FoldOutcome<TState> {
  assert(events.length <= EVENTS_PER_FOLD_MAX, `foldEvents: batch of ${events.length} exceeds ${EVENTS_PER_FOLD_MAX}`);
  for (let i = 0; i < events.length; i += 1) {
    const event = events[i]!;
    assert(event.clientSeq > 0, 'foldEvents: every stored event has clientSeq > 0');
    assert(event.si > 0 || event.serverTs === 0, 'foldEvents: an unsent event carries no server timestamp');
    if (i > 0) assert(events[i - 1]!.li < event.li, 'foldEvents: events sorted by li ascending');
  }
  const results: unknown[] = [];
  if (events.length === 0) return { state, results };
  const next = produce(state, (draft) => {
    for (const event of events) {
      const decoded = decodeValue(event.value);
      const reducer = resolveReducer(def, decoded.kind);
      const result = reducer.apply(draft as TState, decoded.payload, metaOf(event));
      if (result === undefined) continue;
      if (collectResults) results.push(finaliseResult(result));
    }
  });
  assert(results.length <= events.length, 'foldEvents: at most one result per event');
  assert(collectResults || results.length === 0, 'foldEvents: replay collects nothing');
  return { state: next, results };
}

// Fold-boundary validation: true iff this decoded event can be folded by this build. A minor
// above what this build knows is a newer payload shape it must not guess at. Legacy kinds carry
// no schema and no minor expectation.
export function validateIncomingEvent<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  eventValue: string,
  eventTypeMinor: number,
): boolean {
  let decoded: { kind?: unknown; payload?: unknown };
  try {
    decoded = JSON.parse(eventValue) as { kind?: unknown; payload?: unknown };
  } catch {
    return false;
  }
  if (typeof decoded.kind !== 'string') return false;
  const eventDef = lookupKind<AnyEventDef<TState>>(def.events, decoded.kind);
  if (eventDef !== undefined) {
    const known = lookupKind(def.wireTypes, decoded.kind);
    assert(known !== undefined, 'validateIncomingEvent: every active kind has a wire type');
    if (eventTypeMinor > known.minor) return false;
    try { eventDef.payload.parse(decoded.payload); } catch { return false; }
    return true;
  }
  return lookupKind(def.legacyEvents, decoded.kind) !== undefined;
}

// The pure helpers lansura's tests fold with. Same names and signatures as before; a mutation
// carries an optional `meta` where it used to carry an envelope.

function syntheticEvent(mutation: AnyMutation, li: number): StoredEvent {
  const meta = mutation.meta ?? FALLBACK_META;
  return {
    li,
    type: '', id: '',
    si: meta.origin === 'remote' ? 1 : 0,
    clientSeq: 1,
    clientId: meta.clientId,
    userId: meta.userId,
    ts: meta.timestamp,
    serverTs: 0,
    typeMajor: 0, typeMinor: 1,
    value: JSON.stringify({ kind: mutation.kind, payload: mutation.payload }),
  };
}

function requireActiveKind<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  kind: string,
): AnyEventDef<TState> {
  const eventDef = lookupKind<AnyEventDef<TState>>(def.events, kind);
  if (eventDef === undefined) throw new Error(`unknown event kind for aggregate "${def.name}": ${kind}`);
  return eventDef;
}

// Folds one committable mutation; legacy kinds are refused because they are no longer
// committable. Returns the new frozen state.
export function applyMutation<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  state: TState,
  mutation: AnyMutation,
): TState {
  return applyMutations(def, state, [mutation]);
}

export function applyMutations<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  state: TState,
  mutations: ReadonlyArray<AnyMutation>,
): TState {
  if (mutations.length === 0) return state;
  for (const mutation of mutations) requireActiveKind(def, mutation.kind);
  const events = mutations.map((m, i) => syntheticEvent(m, i + 1));
  const outcome = foldEvents(def, state, events, false);
  assert(outcome.results.length === 0, 'applyMutations: replay fold collects no results');
  return outcome.state;
}

// The result of folding one mutation, for callers that want the report and the state.
export function applyMutationWithResult<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  state: TState,
  mutation: AnyMutation,
): { state: TState; result: unknown } {
  requireActiveKind(def, mutation.kind);
  const outcome = foldEvents(def, state, [syntheticEvent(mutation, 1)], true);
  assert(outcome.results.length <= 1, 'applyMutationWithResult: one mutation, at most one result');
  return { state: outcome.state, result: outcome.results[0] };
}

// Derives the undo event from what the apply reports, or undefined when the event is not
// undoable (no `undo`, apply folded to nothing, or `undo` declined). The old signature is kept:
// `state` is the state the mutation is applied to.
export function invertMutation<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  state: TState,
  mutation: AnyMutation,
): AnyMutation | undefined {
  const eventDef = requireActiveKind(def, mutation.kind);
  if (eventDef.undo === undefined) return undefined;
  const { result } = applyMutationWithResult(def, state, mutation);
  if (result === undefined) return undefined;
  return undoOf(def, mutation.kind, eventDef, result);
}

// The undo event must be a declared kind; a typo surfaces here, naming both kinds, not at the
// far end of an undo stack.
export function undoOf<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  kind: string,
  eventDef: AnyEventDef<TState>,
  result: unknown,
): UndoEvent | undefined {
  assert(eventDef.undo !== undefined, 'undoOf: the kind declares undo');
  const undo = eventDef.undo(result);
  if (undo === undefined) return undefined;
  if (lookupKind(def.events, undo.kind) === undefined) {
    throw new Error(`undo for "${kind}" returned unknown kind "${undo.kind}"`);
  }
  return undo;
}

export function checkPrecondition<TState>(
  def: AggregateDef<TState, any>, // eslint-disable-line @typescript-eslint/no-explicit-any
  state: TState,
  mutation: AnyMutation,
): boolean {
  const eventDef = lookupKind<AnyEventDef<TState>>(def.events, mutation.kind);
  if (eventDef === undefined) return false;
  if (eventDef.precondition === undefined) return true;
  return eventDef.precondition(state, mutation.payload);
}

// A fresh deep-frozen copy, so freezing one handle's seed cannot freeze the definition's
// shared literal.
export function initialFrozenState<TState>(def: AggregateDef<TState, any>): TState { // eslint-disable-line @typescript-eslint/no-explicit-any
  return freeze(structuredClone(def.initialState), true);
}

// Decodes a stored event to a mutation carrying its meta. Kept for callers that inspect the log.
export function decodePersistedEvent(event: StoredEvent): AnyMutation {
  const decoded = decodeValue(event.value);
  return { kind: decoded.kind, payload: decoded.payload, meta: metaOf(event) };
}
